import { ReactNode, useCallback, useMemo, useRef, useState } from "react";
import PaginatorContext, { IPaginationModel } from "./PaginatorContext";

interface Props {
  children: ReactNode;
}

const PaginatorProvider = ({ children }: Props) => {
  const [models, setModels] = useState<Record<string, IPaginationModel>>({});

  // Per-key cache of the "never explicitly set" default model. Without this,
  // getModel() would mint a fresh object literal on every render for any key the
  // user has not yet paged/resized. Consumers pass that object straight into
  // useFetch(), whose effect depends on it BY REFERENCE — so a new identity each
  // render means: fetch -> setState -> re-render -> new object -> effect refires
  // -> fetch -> ... an unbounded refetch loop. Caching the default keeps the
  // reference stable until a real setModel writes a value for that key.
  const defaultsRef = useRef<Record<string, IPaginationModel>>({});

  const getModel = useCallback(
    (key: string, defaultPageSize: number): IPaginationModel => {
      const stored = models[key];
      if (stored) return stored;
      const cached = defaultsRef.current[key];
      // Re-mint only if the requested default page size changed for this key,
      // so a caller passing a different defaultPageSize is still honored.
      if (cached && cached.pageSize === defaultPageSize) return cached;
      const fresh: IPaginationModel = { page: 0, pageSize: defaultPageSize };
      defaultsRef.current[key] = fresh;
      return fresh;
    },
    [models],
  );

  const setModel = useCallback((key: string, model: IPaginationModel) => {
    setModels((prev) => ({ ...prev, [key]: model }));
  }, []);

  const value = useMemo(() => ({ getModel, setModel }), [getModel, setModel]);

  return (
    <PaginatorContext.Provider value={value}>
      {children}
    </PaginatorContext.Provider>
  );
};

export default PaginatorProvider;
